import { isAxiosError } from 'axios'
import { apiClient } from './client'
import type {
  JobSchedule,
  JobStatus,
  ListSort,
  PostnordJob,
  ShipmentListResult,
  ShipmentStatus,
  ShipmentWithCustomer,
  ShipmentWriteOff
} from './types'

const POSTNORD_RUN_PATHS: Record<PostnordJob, string> = {
  shipment_motor: '/shipments/postnord/run-booking',
  shipment_tracking: '/shipments/postnord/run-sync'
}

export interface ListShipmentsParams {
  status?: ShipmentStatus[]
  search?: string
  customer_id?: number
  from?: string
  to?: string
  sort?: ListSort
  limit?: number
  after?: number
  before?: number
}

function normalizeListResult(data: unknown): ShipmentListResult {
  if (Array.isArray(data)) {
    const shipments = data as ShipmentWithCustomer[]

    return { shipments, total: shipments.length, has_next: false, has_previous: false }
  }

  if (data && typeof data === 'object' && 'shipments' in data) {
    const result = data as Partial<ShipmentListResult>
    const shipments = Array.isArray(result.shipments) ? result.shipments : []

    return {
      shipments,
      total: typeof result.total === 'number' ? result.total : 0,
      has_next: result.has_next === true,
      has_previous: result.has_previous === true
    }
  }

  return { shipments: [], total: 0, has_next: false, has_previous: false }
}

export async function listShipments(params: ListShipmentsParams = {}, signal?: AbortSignal): Promise<ShipmentListResult> {
  const { data } = await apiClient.get<unknown>('/shipments', { params, signal })

  return normalizeListResult(data)
}

export interface PrintLabelsParams {
  // Kommaseparerade försändelse-id:n. Backend skriver ut exakt dessa.
  ids: string
}

// Svaret är en blob även vid fel, så JSON-felet måste läsas ur bloben för att bli läsbart.
async function readBlobErrorMessage(error: unknown): Promise<string> {
  if (!isAxiosError(error) || !(error.response?.data instanceof Blob)) {
    return 'Kunde inte hämta fraktsedlarna just nu.'
  }

  try {
    const parsed: unknown = JSON.parse(await error.response.data.text())

    if (parsed && typeof parsed === 'object' && 'message' in parsed && typeof parsed.message === 'string') {
      return parsed.message
    }
  } catch {
    // Ignorerat: svaret var ingen JSON, standardtexten nedan används i stället.
  }

  return 'Kunde inte hämta fraktsedlarna just nu.'
}

export async function fetchShipmentLabels(params: PrintLabelsParams): Promise<Blob> {
  try {
    const { data } = await apiClient.get<Blob>('/shipments/labels', { params, responseType: 'blob' })

    return data
  } catch (error) {
    throw new Error(await readBlobErrorMessage(error), { cause: error })
  }
}

export async function getShipmentById(id: number): Promise<ShipmentWithCustomer> {
  const { data } = await apiClient.get<ShipmentWithCustomer>(`/shipments/${id}`)

  return data
}

export async function removeShipment(id: number): Promise<ShipmentWithCustomer> {
  const { data } = await apiClient.post<ShipmentWithCustomer>(`/shipments/${id}/remove`)

  return data
}

export async function writeOffShipment(id: number): Promise<ShipmentWriteOff> {
  const { data } = await apiClient.post<ShipmentWriteOff>(`/shipments/${id}/write_off`)

  return data
}

export async function returnShipmentByCustomer(id: number): Promise<ShipmentWriteOff> {
  const { data } = await apiClient.post<ShipmentWriteOff>(`/shipments/${id}/customer_return`)

  return data
}

// Fält som inte skickas lämnas orörda. null återställer standardregeln.
export interface ShipmentBillingChanges {
  due_date?: string | null
  invoice_date?: string | null
  invoice_before_dispatch?: boolean
}

export async function updateShipmentBilling(id: number, changes: ShipmentBillingChanges): Promise<ShipmentWithCustomer> {
  const { data } = await apiClient.patch<ShipmentWithCustomer>(`/shipments/${id}/billing`, changes)

  return data
}

export async function listPostnordJobStatuses(): Promise<JobStatus<PostnordJob>[]> {
  const { data } = await apiClient.get<JobStatus<PostnordJob>[]>('/shipments/postnord/runs')

  return data
}

export async function runPostnordJob(job: PostnordJob): Promise<void> {
  await apiClient.post(POSTNORD_RUN_PATHS[job])
}

export async function updatePostnordSchedule(job: PostnordJob, cron: string): Promise<JobSchedule> {
  const { data } = await apiClient.patch<JobSchedule>(`/shipments/postnord/schedules/${job}`, { cron })

  return data
}
